import { cx } from "@emotion/css";
import { ArrowLeftRight, ArrowRight } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
	PrimaryButton,
	SoftPrimaryButton,
} from "src/components/Default/LekUI/Button";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { getService } from "src/helpers/isPes";
import { useFontSize } from "src/hooks/useFontSize";
import { useSetService } from "src/hooks/useUser";
import LekIcon from "src/images/Icons/lekIcon.svg";
import PesIcon from "src/images/Icons/pesIcon.svg";

type Service = "LEK" | "PES";

interface ServiceCardProps {
	service: Service;
	hasAccess: boolean;
	isCurrent: boolean;
}

const ServiceCard = ({ service, hasAccess, isCurrent }: ServiceCardProps) => {
	const { t } = useTranslation();
	const key = service.toLowerCase();
	const Icon = service === "PES" ? PesIcon : LekIcon;
	const { modalTextFontSizeClass, modalHeaderFontSizeClass, fontSizeClass } =
		useFontSize();

	return (
		<div
			className={cx(
				"flex items-center gap-12 p-16 border-medium-grey border-radius-12 bg-white",
				hasAccess && "item-selected",
			)}
		>
			<div className="grey-square">
				<Icon
					className={cx(
						"icon-big",
						service === "PES" ? "icon-primary-orange" : "icon-primary",
					)}
				/>
			</div>
			<div className="flex flex-col items-start gap-4 flex-1">
				<h4 className={cx(modalHeaderFontSizeClass, "m-0")}>
					{t(`auth.services.${key}.name`)}
				</h4>
				<span className={modalTextFontSizeClass}>
					{t(`auth.services.${key}.description`)}
				</span>
			</div>
			<div className="flex flex-col items-end gap-4">
				{isCurrent && (
					<span className={cx(fontSizeClass, "text-grey")}>
						{t("modals.wrongExam.youAreHere")}
					</span>
				)}
				<span
					className={cx("badge", hasAccess ? "badge--success" : "badge--error")}
				>
					{t(
						hasAccess
							? "modals.wrongExam.hasAccess"
							: "modals.wrongExam.noAccess",
					)}
				</span>
			</div>
		</div>
	);
};

interface WrongServiceModalProps {
	targetService: Service;
	onStay: () => void;
}

export const WrongServiceModal = ({
	targetService,
	onStay,
}: WrongServiceModalProps) => {
	const { t } = useTranslation();
	const { mutateAsync, isPending: isLoading } = useSetService() as unknown as {
		mutateAsync: (service: Service) => Promise<unknown>;
		isPending: boolean;
	};
	const currentService = getService() as Service;
	const { modalTextFontSizeClass } = useFontSize();

	return (
		<Modal onClose={onStay}>
			<div className="flex flex-col gap-24" data-test-id="wrong-service-modal">
				<ModalHeader
					title={t("modals.wrongExam.title")}
					Icon={ArrowLeftRight}
					hasCloseButton
				/>
				<p className={cx(modalTextFontSizeClass, "text-dark-grey m-0")}>
					<Trans
						i18nKey="modals.wrongExam.description"
						values={{ current: currentService, target: targetService }}
						components={[<strong key="current" />, <strong key="target" />]}
					/>
				</p>
				<div className="flex flex-col gap-12">
					<ServiceCard service={currentService} hasAccess={false} isCurrent />
					<ServiceCard service={targetService} hasAccess isCurrent={false} />
				</div>
				<ModalFooter className="flex-col-mobile">
					<SoftPrimaryButton
						className="w-100 btn-size-big border-radius-12"
						onClick={onStay}
						data-test-id="wrong-service-stay-button"
					>
						{t("modals.wrongExam.stay", { service: currentService })}
					</SoftPrimaryButton>
					<PrimaryButton
						className="w-100 btn-size-big border-radius-12"
						isLoading={isLoading}
						onClick={() => mutateAsync(targetService)}
						endIcon={<ArrowRight className="icon-small" />}
						data-test-id="wrong-service-switch-button"
					>
						{t("modals.wrongExam.switch", { service: targetService })}
					</PrimaryButton>
				</ModalFooter>
			</div>
		</Modal>
	);
};
